import './RatesFilter.less';

import {
  AimOutlined,
  ArrowRightOutlined,
  CloseOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import { Button, Form, Input, Select, Tooltip } from 'antd';
import { isEmpty } from 'lodash';
import React, { useEffect } from 'react';
import removeAccents from 'remove-accents';

import { Carrier } from '../../models/Carrier';
import { DefaultMode } from '../../models/Mode';
import { RateFilter } from '../../models/Rate';
import Toggle from '../common/Toggle';

interface IProps {
  showAllCarriers?: boolean;
  filter: RateFilter | null;
  carriers?: Carrier[];
  modes?: DefaultMode[];
  onFilterChange: (filter: RateFilter | null) => void;
}

const RatesFilter: React.FC<IProps> = ({
  carriers,
  filter,
  modes,
  onFilterChange,
  showAllCarriers,
}) => {
  const [form] = Form.useForm();

  const [isSeparateSearch, setIsSeparateSearch] = React.useState(false);
  const [originValue, setOriginValue] = React.useState<string>('');
  const [destinationValue, setDestinationValue] = React.useState<string>('');

  useEffect(() => {
    const searchType = filter?.searchType ?? 'unified';
    form.setFieldsValue({
      carrierIds: filter?.carrierIds,
      destination: filter?.destination,
      modeNames: filter?.modeNames,
      origin: filter?.origin,
      searchType,
    });
    setIsSeparateSearch(searchType === 'separate');
    setOriginValue(filter?.origin || '');
    setDestinationValue(filter?.destination || '');
  }, [
    filter?.searchType,
    filter?.carrierIds,
    filter?.modeNames,
    filter?.destination,
    filter?.origin,
    form,
  ]);

  const handleSearchTypeChange = (value: string) => {
    const isUnifiedSearch = value === 'unified';
    setIsSeparateSearch(!isUnifiedSearch);
    // this used to be a field but now it's a toggle
    form.setFieldValue('searchType', value);
  };

  const filterOption = (input: any, option: any) => {
    const normalizedInput = removeAccents((input ?? '').toLowerCase());
    const normalizedLabel = removeAccents((option?.label ?? '').toLowerCase());
    return normalizedLabel.includes(normalizedInput);
  };

  const toggleQuotes = (fieldName: 'origin' | 'destination') => {
    const currentValue =
      fieldName === 'origin' ? originValue : destinationValue;
    const trimmed = currentValue.trim();
    const hasQuotes =
      (trimmed.startsWith('"') && trimmed.endsWith('"')) ||
      (trimmed.startsWith("'") && trimmed.endsWith("'"));

    const newValue = hasQuotes ? trimmed.slice(1, -1).trim() : `"${trimmed}"`;

    form.setFieldValue(fieldName, newValue);

    if (fieldName === 'origin') {
      setOriginValue(newValue);
    } else {
      setDestinationValue(newValue);
    }
  };

  const hasQuotes = (fieldName: 'origin' | 'destination') => {
    const value = fieldName === 'origin' ? originValue : destinationValue;
    const trimmed = value.trim();
    return (
      (trimmed.startsWith('"') && trimmed.endsWith('"')) ||
      (trimmed.startsWith("'") && trimmed.endsWith("'"))
    );
  };

  const handleOriginChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setOriginValue(value);
  };

  const handleDestinationChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setDestinationValue(value);
  };

  const handleOriginBlur = () => {
    const formValue = form.getFieldValue('origin') || '';
    setOriginValue(formValue);
  };

  const handleDestinationBlur = () => {
    const formValue = form.getFieldValue('destination') || '';
    setDestinationValue(formValue);
  };

  return (
    <Form
      className="rates-filter"
      form={form}
      name="rates-filter-form"
      onFinish={onFilterChange}
    >
      <Form.Item className="rates-filter__form-item" label="Search Rates">
        {showAllCarriers && (
          <Form.Item
            className="rates-filter__filter-item rates-filter__carrier-select"
            name="carrierIds"
          >
            <Select
              allowClear
              showSearch
              filterOption={filterOption}
              maxTagCount="responsive"
              maxTagPlaceholder={(omittedValues) =>
                omittedValues.length > 0 ? (
                  <Tooltip
                    title={omittedValues.map(({ label }) => label).join(', ')}
                  >
                    <span>
                      {omittedValues[0]?.label}
                      {omittedValues.length > 1 &&
                        ` + ${omittedValues.length - 1} more...`}
                    </span>
                  </Tooltip>
                ) : null
              }
              mode="multiple"
              optionFilterProp="children"
              placeholder="Carrier"
              onChange={(value) => {
                if (Array.isArray(value) && value.length === 0) {
                  form.setFieldValue('carrierIds', undefined);
                }
              }}
            >
              {carriers?.map((carrier) => (
                <Select.Option
                  key={carrier.id}
                  label={carrier.name}
                  value={carrier.id}
                >
                  {carrier.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        )}

        <Form.Item
          className="rates-filter__filter-item rates-filter__mode-select"
          name="modeNames"
        >
          <Select
            allowClear
            showSearch
            filterOption={filterOption}
            maxTagCount="responsive"
            maxTagPlaceholder={(omittedValues) =>
              omittedValues.length > 0 ? (
                <Tooltip
                  title={omittedValues.map(({ label }) => label).join(', ')}
                >
                  <span>
                    {omittedValues[0]?.label}
                    {omittedValues.length > 1 &&
                      ` + ${omittedValues.length - 1} more...`}
                  </span>
                </Tooltip>
              ) : null
            }
            mode="multiple"
            placeholder="Mode"
            onChange={(value) => {
              if (Array.isArray(value) && value.length === 0) {
                form.setFieldValue('modeNames', undefined);
              }
            }}
          >
            {modes?.map((mode) => (
              <Select.Option key={mode.id} label={mode.name} value={mode.name}>
                {mode.name}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item
          className="rates-filter__filter-item rates-filter__toggle"
          name="searchType"
        >
          <Tooltip title="Origin/Destination Search">
            <Toggle
              onChange={(checked) => {
                handleSearchTypeChange(checked ? 'separate' : 'unified');
              }}
            />
          </Tooltip>
        </Form.Item>

        <Form.Item
          className="rates-filter__filter-item rates-filter__origin-input"
          name="origin"
          style={{
            margin: '0 8px',
            width: 'calc(28% - 8px)',
          }}
        >
          <Input
            addonAfter={
              <Tooltip title="Exact match (ignore parent cities)">
                <Button
                  htmlType="button"
                  icon={<AimOutlined />}
                  size="small"
                  style={{ border: 'none', boxShadow: 'none' }}
                  type={hasQuotes('origin') ? 'primary' : 'default'}
                  onClick={() => toggleQuotes('origin')}
                >
                  Exact
                </Button>
              </Tooltip>
            }
            placeholder={isSeparateSearch ? 'Origin' : 'Location'}
            onBlur={handleOriginBlur}
            onChange={handleOriginChange}
          />
        </Form.Item>

        {isSeparateSearch && (
          <>
            <ArrowRightOutlined
              className="rates-filter__arrow-icon"
              id="dest-arrow"
            />

            <Form.Item
              className="rates-filter__filter-item rates-filter__destination-input destination-container"
              dependencies={['searchType']}
              name="destination"
            >
              <Input
                addonAfter={
                  <Tooltip title="Exact match (ignore parent cities)">
                    <Button
                      htmlType="button"
                      icon={<AimOutlined />}
                      size="small"
                      style={{ border: 'none', boxShadow: 'none' }}
                      type={hasQuotes('destination') ? 'primary' : 'default'}
                      onClick={() => toggleQuotes('destination')}
                    >
                      Exact
                    </Button>
                  </Tooltip>
                }
                placeholder="Destination"
                onBlur={handleDestinationBlur}
                onChange={handleDestinationChange}
              />
            </Form.Item>
          </>
        )}

        <Form.Item className="rates-filter__filter-item rates-filter__search-button">
          <Tooltip title="Search">
            <Button
              htmlType="submit"
              icon={<SearchOutlined />}
              type="primary"
            />
          </Tooltip>
        </Form.Item>

        <Form.Item className="rates-filter__filter-item rates-filter__reset-button">
          <Tooltip title="Reset">
            <Button
              icon={<CloseOutlined />}
              style={{ visibility: isEmpty(filter) ? 'hidden' : 'visible' }}
              type="text"
              onClick={() => onFilterChange(null)}
            />
          </Tooltip>
        </Form.Item>
      </Form.Item>
    </Form>
  );
};

export default RatesFilter;
